import { Component, inject, ViewChild, ElementRef, OnInit, TemplateRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BsDatepickerConfig } from "ngx-bootstrap/datepicker";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ReCaptchaV3Service } from "ng-recaptcha";
import { ActivatedRoute, Router } from '@angular/router';

// SERVICES
import { ApiService } from "src/app/Services/api.service";
import { UtilsService } from 'src/app/Services/utils.service';
import { ExternalService } from "src/app/Services/external.service";

type Reservation = {
  id: string,
  type: string,
  identifier: string,
  status: string,
  name: string,
  user_id: string,
  ref: string,
  notes: string,
  vat: string,
  email: string,
  phone: string,
  createdAt: Date,
  municipality: string,
  line: string,
  operator: string,
  stop: string,
  date: string,
  hour: string,
};

interface ScheduleData {
  pattern: { id: string; desc: string };
  times: any[]; 
}

@Component({
  selector: 'app-reservar-detalhe',
  templateUrl: './reservar-detalhe.component.html',
  standalone: false
})
export class ReservarDetalheComponent implements OnInit {
  @ViewChild('top') top: ElementRef | undefined;
  title = 'cim-tamega-sousa';

  private reservationId: string = '';
  private token: string = '';

  public infoLoaded = 0;
  public reservation = {} as Reservation;
  public hasError: boolean = false;

  // Edit
  editLimitInDays: number = 2;
  lowerLimitInDays: number = 2;
  upperLimitInDays: number = 8;
  errorWorkingHours: boolean = false;

  canEdit: boolean = false;
  canDelete: boolean = false;

  editEnabled: boolean = false;
  showEditForm: boolean = true;
  submitted : boolean = false;
  inProgress: boolean = false;
  successEdit: boolean = false;
  errorEdit: boolean = false;
  
  municipalities: any[] = [];
  lines: any[] = [];
  stops: any[] = [];
  hours: any[] = [];
  selectedLine: string = ''
  line: string = '';
  lineOperator: string = '';
  lineOperatorLogos: any[] = [];
  unknownOperator: boolean = false;

  lineOperatorEdit: string = '';
  lineOperatorLogosEdit: any[] = [];
  unknownOperatorEdit: boolean = false;

  // Configurações Datepicker
  bsValueDay = new Date();
  bsConfig: Partial<BsDatepickerConfig> = {};
  minDate  = new Date();
  maxDate = new Date();

  modalService = inject(NgbModal);

  formEditReserve!: FormGroup;

  submittedDelete: boolean = false;
  formCancelReserve!: FormGroup;

  constructor(
    private recaptchaV3Service: ReCaptchaV3Service,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private utilsService: UtilsService,
    private externalService: ExternalService,
    private formBuilder: FormBuilder
  ) {
    
    this.activatedRoute.queryParams
    .subscribe(queryParams => {
      queryParams['id'] != undefined ? this.reservationId = queryParams['id'] : this.goHome();
    })

    this.recaptchaV3Service.execute('importantAction')
      .subscribe((token: string) => {
        this.token = token;
        if (this.reservationId != '') this.getReservationDetail(this.reservationId)
      });

    this.setDateRange();
    this.configureDatepicker();
  }

  ngOnInit(){
  }

  get fE(): { [key: string]: AbstractControl } {
    return this.formEditReserve.controls;
  }

  get fCR(): { [key: string]: AbstractControl } {
    return this.formCancelReserve.controls;
  }

  getRecaptchaToken() {
    this.recaptchaV3Service.execute('importantAction')
      .subscribe((token: string) => {
        this.token = token;
      });
  }

  getReservationDetail(id: string){
    this.infoLoaded = 0;
    this.hasError = false;
    this.apiService.getReservation(id, this.token)
    .subscribe({
      next: data => {

        let response = data['body']

        if(response['type'] != 'RESERVATION'){
          this.router.navigate(['/reclamacao-detalhe'], { queryParams: { id: id } })
        }

        this.reservation.id = response['id']
        this.reservation.identifier = response['identifier']
        this.reservation.status = response['status']
        this.reservation.name = response['requested_by_name']
        this.reservation.user_id = response['requested_by_id']
        this.reservation.ref = response['ref']
        this.reservation.notes = response['notes']
        this.reservation.vat = response['extra']['vat']
        this.reservation.email = response['requested_by_email']
        this.reservation.phone = response['extra']['phone']
        this.reservation.createdAt = response['created_at']
        this.reservation.municipality = response['extra']['municipality']
        this.reservation.line = response['extra']['line'] != ' - ' ? response['extra']['line'] : ''
        this.reservation.stop = response['extra']['stop']
        this.reservation.date = response['occurrence_date']
        this.reservation.hour = response['extra']['time']
        this.reservation.type = response['type']

        const now = new Date()
        const reservationDate = new Date(this.reservation.date);
        reservationDate.setHours(0, 0, 0, 0);
        const [hours, minutes] = this.reservation.hour.split(':').map(Number);
        reservationDate.setHours(hours, minutes);

        const totalWorkingHoursUntilReservation = this.utilsService.getWorkingHours(new Date(), reservationDate);

        if (this.reservation.status == 'APPROVED' && now > reservationDate){
          this.reservation.status = 'FINISHED';
        } else {
          this.reservation.status = this.reservation.status;

          let lowerLimitInHours = this.editLimitInDays * 24;
          this.errorWorkingHours = totalWorkingHoursUntilReservation < lowerLimitInHours;
        }

        this.canEdit = this.reservation.status == 'PENDING' || this.reservation.status == 'APPROVED';
        this.canDelete = this.reservation.status == 'PENDING' || this.reservation.status == 'APPROVED';

        let op = response['ref'].split('##')[0]

        if (op != ''){
          switch (true) {
            case op == 'TRD':
              this.reservation.operator = 'Transdev Tâmega e Sousa, Mobilidade e Transporte Público, Unipessoal, Lda.';
              this.lineOperatorLogos.push('transdev');
              break;
            case op == 'EXP':
              this.reservation.operator = 'Expressdiagram - Transportes Rodoviários Lda.';
              this.lineOperatorLogos.push('valpi');
              this.lineOperatorLogos.push('rodonorte');
              break;
            case op == 'ETR':
              this.reservation.operator = 'Etapas Remotas - Transportes Rodoviários Lda.';
              this.lineOperatorLogos.push('valpi');
              this.lineOperatorLogos.push('rodonorte');
              break;
            case op == 'RDR':
              this.reservation.operator = 'Rota da Razão - Transportes Rodoviários Lda.'
                this.lineOperatorLogos.push('rodonorte');
                this.lineOperatorLogos.push('avlandim');
              break;
            default:
              this.reservation.operator = '';
          }
        }

        this.infoLoaded++;

        this.getRecaptchaToken();
      },
      error: er => {
        this.infoLoaded++;
        this.hasError = true;
      }
    });
  }

  enableEditReservation(){
    // FORMULÁRIO EDITAR RESERVA
    this.formEditReserve = this.formBuilder.group({
      municipality: ['', [Validators.required]],
      line:         ['', [Validators.required]],
      stop:         ['', [Validators.required]],
      hour:         ['', [Validators.required]],
      date:         [this.utilsService.addWorkDays(new Date(), this.lowerLimitInDays), [Validators.required]],
      message: [
          '',
          [
            Validators.required,
            Validators.pattern(/^( *|\S.*)(\r?\n( *|\S.*))*$/),
            Validators.minLength(15),
            Validators.maxLength(500),
          ],
        ], 
      // Consentimentos
      acceptTermsEdit: [false, [Validators.requiredTrue]],
      acceptTermsAccessibilityEdit: [false, [Validators.requiredTrue]],
    });
    this.onChanges();

    this.getMunicipalities();

    this.editEnabled = true;
  }

  onChanges(){
    this.formEditReserve.get('municipality')?.valueChanges.subscribe(
      val => {
        this.lineOperator = '';
        this.formEditReserve.get('line')?.setValue('');
        this.formEditReserve.get('stop')?.setValue('');

        if(val != '') {
          this.listRoutesByMunicipality(val);
        } else {
          this.lines.splice(0);
          this.stops.splice(0); 
        }
      }
    )

    this.formEditReserve.get('line')?.valueChanges.subscribe(
      val => {
        this.lineOperatorEdit = ''
        this.lineOperatorLogosEdit = [];
        if(val != ''){
          this.formEditReserve.get('stop')?.setValue('');

          let ln = this.lines.find(ln => ln.codeCIM === val)

          if (ln != undefined) {
            this.line = ln

            if(ln['operator'] == 'DESCONHECIDO' || ln['operator'] == ''){
              this.lineOperatorEdit = 'CIMTS'
              this.unknownOperatorEdit = true;
            } else {

              this.lineOperatorLogosEdit = ln['operatorLogos'];
              this.unknownOperatorEdit = false;
              switch (true) {
                case ln['operator'].toLowerCase().startsWith("transdev"):
                  this.lineOperatorEdit = 'TRD';
                  break;
                case ln['operator'].toLowerCase().startsWith("expressdiagram"):
                  this.lineOperatorEdit = 'EXP';
                  break;
                case ln['operator'].toLowerCase().startsWith("etapas"):
                  this.lineOperatorEdit = 'ETR';
                  break;
                case ln['operator'].toLowerCase().startsWith("remote"):
                  this.lineOperatorEdit = 'ETR';
                  break;
                case ln['operator'].toLowerCase().startsWith("rota"):
                  this.lineOperatorEdit = 'RDR';
                  break;
                default:
                  this.lineOperatorEdit = 'CIMTS';
              }
            }

            this.selectedLine = ln['codeCIM'];
            this.listStopsByRoute(this.selectedLine);
          }
          
        }
      }
    )

    this.formEditReserve.get('stop')?.valueChanges.subscribe(
      val => {

        if(val != null && val != '' && this.formEditReserve.get('date')?.value != ''){
          let stop = val;
          let date = this.utilsService.formatDate(this.formEditReserve.get('date')?.value);

          this.getStopScheduleDay(stop, date)
        }
      }
    )

    this.formEditReserve.get('date')?.valueChanges.subscribe(
      val => {

        if(val != null && val != '' && this.formEditReserve.get('stop')?.value != ''){
          this.errorWorkingHours = false;
          let stop = this.formEditReserve.get('stop')?.value;
          let date = this.utilsService.formatDate(val);

          this.getStopScheduleDay(stop, date)
        }
      }
    )

    this.formEditReserve.get('hour')?.valueChanges.subscribe(
      val => {
        if(val != null && val != '' && this.formEditReserve.get('date')?.value != ''){
          this.errorWorkingHours = false;

          let day = this.formEditReserve.get('date')?.value
          let hour = val

          const target = new Date(day);
          target.setHours(0, 0, 0, 0);

          const [hours, minutes] = hour.split(':').map(Number);
          target.setHours(hours, minutes);

          const totalWorkingHoursUntilReservation = this.utilsService.getWorkingHours(new Date(), target);

          let lowerLimitInHours = this.lowerLimitInDays * 24;
          let upperLimitInHours = this.upperLimitInDays * 24;

          this.errorWorkingHours = totalWorkingHoursUntilReservation < lowerLimitInHours || totalWorkingHoursUntilReservation > upperLimitInHours
        }
      }
    )

  }

  getMunicipalities(){
    this.externalService.getMunicipalities().subscribe(
        data => {

          this.municipalities.splice(0)
          
          data.forEach(element => {
            let mun = {
              name: element['pDescricao'],
              code: element['pDescricao'],
              key: element['pKey'],
              region: element['pNUTIII']
            }

            this.municipalities.push(mun)
          });

          this.municipalities = this.municipalities.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }) );
        },
        error => {
          this.router.navigate(['/500']);
        } 
      )
  }

  listRoutesByMunicipality(municipality: string){
    this.lines.splice(0);

    this.externalService.getRoutesByMunicipality(municipality).subscribe(
      {
        next: data => {
          data.forEach(item => {
            let opCode = '';
            let opLogos = [];
            switch (true) {
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("transdev"):
                opCode = 'Transdev Norte';
                opLogos.push('transdev');
                break;
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("expressdiagram"):
                opCode = 'Valpi, Rodonorte';
                opLogos.push('valpi');
                opLogos.push('rodonorte');
                break;
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("etapas"):
                opCode = 'AVPacense, Valpi';
                opLogos.push('avpacense');
                opLogos.push('valpi');
                break;
              case (item['pdesigOperador'] as any).toLowerCase().startsWith("rota"):
                opCode = 'Rodonorte, AVLandim';
                opLogos.push('rodonorte');
                opLogos.push('avlandim');
                break;
              default:
                opCode = '';
            }
            let line = {
              number: item['pCodCarOperador'],
              name: item['pDesignacao'],
              municipality: municipality,
              operator: item['pdesigOperador'],
              operatorCode: opCode,
              operatorLogos: opLogos,
              codeCIM: item['pCodCarCIM'],
              color: item['pColorHex']
            }

            this.lines.push(line);
          });
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    )
  }

  listStopsByRoute(route_id: string){
    this.stops.splice(0);

    let params = {  
      "offset": 0,
      "limit": 2000,
      "include": true,
      route_ids: [route_id]
    }

    this.apiService.getStopsByRoute(params).subscribe(
      {
        next: data => {
          let results = data['body']['results'];
          
          results.forEach((item: any) => {
            let stop = {
              id: '1:' + item['stop_id'],
              code: item['stop_id'],
              name: item['stop_name'],
              municipality: '',
              lat: item['lat'],
              lng: item['lon'],
              zone: item['zone_id']
            }

            this.stops.push(stop);

            this.stops.sort((a: any, b: any) => a.name < b.name ? -1 : 1 );
          });
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    )
  }

  getStopScheduleDay(stop: string, day: string){
    this.hours = []
    if(stop != '' && day != ''){
      this.externalService.getStopScheduleDay(stop,day).subscribe(
        {
          next: data => {
            let pattern = ':' + this.selectedLine + ':'

            const response = data as ScheduleData[];

            response.forEach((entry) => {
              if (entry.pattern.id.includes(pattern)) {

                if (entry.times.length > 0) {

                  entry.times.forEach((element: any) => {
                    let time = {
                      realtimeArrival: element['realtimeArrival'],
                      realtimeArrivalDesc: this.utilsService.secondsToTime(element['realtimeArrival']),
                    }

                    this.hours.push(time)
                  });
                  
                }
              }
            });

            this.hours.sort((a,b) => (a.realtimeArrival > b.realtimeArrival) ? 1 : -1 )

          },
          error: er => {

          }
        }
      )
    }
  }

  setDateRange(): void {
    const today = new Date();
    this.minDate.setDate(today.getDate());
    this.maxDate.setDate(today.getDate() + 10);
  }

  configureDatepicker(): void {
    this.bsConfig = {
      dateInputFormat: 'DD-MM-YYYY',
      showWeekNumbers: false,
      isAnimated: true,
      minDate: this.utilsService.addWorkDays(new Date(), this.lowerLimitInDays),
      maxDate: this.utilsService.addWorkDays(new Date(), this.upperLimitInDays)
    };
  }

  saveEditReservation(){
    this.showEditForm = false;
    this.submitted = true;
    this.inProgress = true;
    this.successEdit = false;
    this.errorEdit = false;

    if (this.formEditReserve.invalid) {
      this.showEditForm = true;
      this.inProgress = false;
      return;
    } else {
      let date = new Date(this.formEditReserve.get('date')?.value).toISOString().split('T')[0] + 'T' + this.formEditReserve.get('hour')?.value + ':00Z'
      let dateSimple = new Date(this.formEditReserve.get('date')?.value).toISOString().split('T')[0]
      let mun = this.municipalities.find(mun => mun.code === this.formEditReserve.get('municipality')?.value)
      let municipality = mun != undefined ? mun['name'] : ''
      let stop = this.stops.find(stop => stop.id === this.formEditReserve.get('stop')?.value)
      let stopNumName = stop['code'] + ' - '+ stop['name']
      let lineNumName = (this.line as any)['number'] + ' - '+ (this.line as any)['name']

      let body = 'Reserva para ' + dateSimple + ', às ' + this.formEditReserve.get('hour')?.value
        + ', na paragem ' +  stopNumName + ' da linha ' + lineNumName + ' (' + municipality + ').'

      let params = {
        requested_by_name: this.reservation.name,
        requested_by_email: this.reservation.email,
        requested_by_id: this.reservation.user_id,
        ref: this.lineOperatorEdit + '##' + (this.line as any)['number'],
        type: 'RESERVATION',
        ref_type: 'OPERATOR',
        status: 'PENDING',
        subject: 'Reserva',
        body: body,
        occurrence_date: date,
        extra:{
            phone: this.reservation.phone,
            time: this.formEditReserve.get('hour')?.value,
            municipality: municipality,
            line: (this.line as any)['number'] + ' - '+ (this.line as any)['name'],
            stop: stop['code'] + ' - '+ stop['name'],
            vat: this.reservation.vat
          },
        reason: this.formEditReserve.get('message')?.value
      }

      this.apiService.editReservation(this.reservation.id, params, this.token).subscribe(
        data => {
        
          this.submitted = false;
          this.inProgress = false;
          this.successEdit = true;
          this.top?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          this.formEditReserve.reset();
          setTimeout(() => {
            this.editEnabled = false;
            this.successEdit = false;
            window.location.reload()
          }, 5000);

        },
        error => {
          this.errorEdit = true;
          this.submitted = false;
          this.inProgress = false;
          this.formEditReserve.get('acceptTermsEdit')?.setValue(false, {emit: false});
          this.formEditReserve.get('acceptTermsAccessibilityEdit')?.setValue(false, {emit: false});
        }
      )

      
    }
  }

  enableCancelReservation(content: TemplateRef<any>){
    // FORMULÁRIO CANCELAR RESERVA
    this.formCancelReserve = this.formBuilder.group({
      message: [
          '',
          [
            Validators.required,
            Validators.pattern(/^( *|\S.*)(\r?\n( *|\S.*))*$/),
            Validators.minLength(15),
            Validators.maxLength(500),
          ],
        ],
    });

    this.openVerticallyCentered(content)
  }

  openVerticallyCentered(content: TemplateRef<any>) {
    this.modalService.open(content, {
      centered: true }
    );
  }

  cancelEditReservation(){
    this.editEnabled = false;
    this.top?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  cancelReservation(){
    this.submittedDelete = true;

    if(this.formCancelReserve.valid){
      let params = {
        status: 'CANCELED',
        reason: this.formCancelReserve.get('message')?.value
      }

      this.apiService.editReservation(this.reservation.id, params, this.token).subscribe(
        data => {
          window.location.reload();
        },
        error => {
          this.errorEdit = true;
          this.submitted = false;
          this.inProgress = false;
        }
      )
    }

  }

  maskEmail(email: string): string {
    return this.utilsService.maskEmail(email)
  }

  maskPhoneNumber(phone: string): string {
    return this.utilsService.maskPhoneNumber(phone)
  }

  maskVat(vat: string): string {
    return this.utilsService.maskVat(vat)
  }

  goHome(){
    this.router.navigate(['/'])
  }
}